import { useMemo } from 'react'

import { uniq } from 'lodash-es'

import { CustomerPropertiesMap } from '../../../models/user/User'
import {
  ExtendedPathwayOption,
  PathwayOptions,
  PROGRAM_CUSTOMER_PROPERTY_TO_PATHWAY_MAP,
} from '../../pathways/constants'
import { ProgramCustomerPropertyId } from '../constants/customerProgram'

export interface AvailabilityStatusReasonInfo {
  propertyName: string
  value?: boolean | number
}
interface AvailabilityInfo {
  available: boolean
  reasons: AvailabilityStatusReasonInfo[]
}

export interface CoachingToolkitAvailabilityInfo {
  individual: AvailabilityInfo
  parent: AvailabilityInfo
}

// Determines forked triage availability
export const useForkedTriageAvailability = ({
  customerPropertiesMap,
  coachingToolkitAvailability,
}: {
  customerPropertiesMap?: CustomerPropertiesMap
  coachingToolkitAvailability?: CoachingToolkitAvailabilityInfo
}) => {
  return useMemo(() => {
    let reasons: Array<AvailabilityStatusReasonInfo> = []

    if (customerPropertiesMap === undefined) return { availableOptions: [], forkedTriageAvailable: false, reasons }
    const libraryDisabled = customerPropertiesMap.libraryDisabled === 'true'
    const shouldHideChildSearchCTA = customerPropertiesMap.shouldHideChildSearchCTA === 'true'
    const directAccessTherapyEnabled = customerPropertiesMap[ProgramCustomerPropertyId.directAccessTherapy] === 'true'

    reasons = [
      { propertyName: 'libraryDisabled', value: libraryDisabled },
      { propertyName: 'shouldHideChildSearchCTA', value: shouldHideChildSearchCTA },
    ]

    let availableOptions: ExtendedPathwayOption[] = []

    PROGRAM_CUSTOMER_PROPERTY_TO_PATHWAY_MAP.forEach((pathwayOptions, propertyName) => {
      if (
        propertyName in customerPropertiesMap &&
        customerPropertiesMap[propertyName as keyof CustomerPropertiesMap] === 'true'
      ) {
        availableOptions = [...availableOptions, ...pathwayOptions]
        reasons.push({ propertyName, value: true })
      } else {
        reasons.push({ propertyName, value: false })
      }
    })

    /**
     * If direct access therapy is enabled, but child search is disabled, remove the available option
     */
    if (directAccessTherapyEnabled && shouldHideChildSearchCTA) {
      availableOptions.splice(availableOptions.indexOf(PathwayOptions.CHILD_SEARCH), 1)
    }

    /**
     * Add toolkits(individual, parenting) if they are available options as well as the reasons they are/aren't available
     */
    if (coachingToolkitAvailability) {
      Object.keys(coachingToolkitAvailability).forEach((toolkit) => {
        const toolkitInfo = coachingToolkitAvailability[toolkit as keyof CoachingToolkitAvailabilityInfo]
        if (toolkitInfo.available) {
          availableOptions.push(
            toolkit === 'individual' ? PathwayOptions.COACHING_TOOLKIT : PathwayOptions.PARENTING_TOOLKIT,
          )
        }
        reasons = [...reasons, ...toolkitInfo.reasons]
      })
    }

    /**
     * Remove duplicate pathway options
     */
    availableOptions = uniq(availableOptions)

    return { availableOptions, forkedTriageAvailable: availableOptions.length > 2, reasons }
  }, [coachingToolkitAvailability, customerPropertiesMap])
}
